import { useState, useEffect, useRef, useCallback } from "react";
import useFetchStaticJson from "../../_foundation/hooks/use-static-json";
import { StyledButton, StyledLink } from "../../styled-mui";
import { ImageLayout } from "../elements/image";
import storeUtil from "../../utils/storeUtil";
import { RegistrationNewsletter } from "../footer/RegistrationNewsletter";
import { Box, Dialog, Fade, GlobalStyles, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router";
import AsyncCall from "../../_foundation/gtm/async.service";
import {
  type TabMessage,
  type PopinContent,
  getPopinStatus,
  isCompleted,
  isSuppressed,
  shouldShowReminder,
  markSeen,
  markCompleted,
  setSuppression,
  clearReminderVisible,
  broadcast,
  getChannel,
  setReminderVisible,
  readState,
  readAndClearBcFallback,
  clearPersistedState,
  sanitizeStateOnMount,
  writeState,
} from "./Welcomepopinutils";

interface WelcomeOfferPopinProps {
  loggedIn?: boolean;
}

const popinKeyframes = (
  <GlobalStyles
    styles={`
    @keyframes wp-slide-up {
      from { opacity: 0; transform: translateY(24px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes wp-slide-in-left {
      from { transform: translateY(-50%) translateX(-100%); }
      to   { transform: translateY(-50%) translateX(0); }
    }
  `}
  />
);

const DiscountBadge = ({ value }: { value: string }) => {
  const match = value?.match(/^(\d+)([€%]?)(.*)$/);
  if (!match)
    return (
      <Typography component="span" sx={{ color: "inherit" }}>
        {value}
      </Typography>
    );
  const [, num, symbol] = match;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "flex-start", lineHeight: 1 }}>
      <Typography
        component="span"
        sx={{
          fontSize: { xs: "78px", md: "95px" },
          fontWeight: 900,
          letterSpacing: "-0.03em",
          lineHeight: 0.9,
          color: "inherit",
        }}>
        {num}
      </Typography>
      <Typography
        component="sup"
        sx={{
          fontSize: { xs: "40px", md: "42px" },
          fontWeight: 700,
          mt: "-6px",
          ml: "2px",
          lineHeight: 1,
          color: "inherit",
        }}>
        {symbol}
      </Typography>
    </Box>
  );
};

interface SideReminderProps {
  onReopen: () => void;
  onDismiss: () => void;
  discount?: string;
}

const SideReminder = ({ onReopen, onDismiss, discount }: SideReminderProps) => (
  <Box
    role="button"
    tabIndex={0}
    aria-label="Rouvrir l'offre de bienvenue"
    title="Voir l'offre de bienvenue"
    onClick={onReopen}
    onKeyDown={(e) => e.key === "Enter" && onReopen()}
    sx={{
      position: "fixed",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 9998,
      bgcolor: "#5D89A2",
      color: "#fff",
      width: 50,
      p: "12px 12px 20px 12px",
      borderRadius: "5px",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "10px",
      transition: "background 0.2s",
      animation: "wp-slide-in-left 0.4s cubic-bezier(0.34, 1.36, 0.64, 1)",
    }}>
    <IconButton
      size="small"
      aria-label="Fermer le rappel"
      onClick={(e) => {
        e.stopPropagation();
        onDismiss();
      }}
      sx={{ width: 16, height: 16, p: 0, color: "#fff", borderRadius: "50%", "&:hover": { bgcolor: "#5D89A2" } }}>
      <CloseIcon sx={{ fontSize: 16 }} />
    </IconButton>
    <Typography
      sx={{
        fontSize: { xs: "10px", sm: "12px" },
        fontWeight: 600,
        letterSpacing: "0.04em",
        writingMode: "vertical-rl",
        textOrientation: "mixed",
        transform: "rotate(180deg)",
        textTransform: "uppercase",
        lineHeight: 1.3,
        m: 0,
        color: "inherit",
      }}>
      {discount}
    </Typography>
  </Box>
);

const WelcomeOfferPopin = ({ loggedIn }: WelcomeOfferPopinProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const { data } = useFetchStaticJson({ file: "newsletter-popin.json", isLocal: false });

  // const STATIC_POPIN_CONTENT: PopinContent = {
  //   activation: "yes",
  //   anchor: "bienvenue",
  //   dateStart: "2026-03-31T00:00:00.000",
  //   dateEnd: "2026-06-09T23:59:59.000",
  //   descriptionHtml: "Offres sur votre prochaine commande ! *",
  //   discount: "20€",
  //   imageDesktop: "/img/OP/2026/Push-Hors-PAC/Visuel_PopinNL.png",
  //   imageMobile: "/img/OP/2026/Push-Hors-PAC/Visuel_PopinNL.png",
  //   reminder: "20€ offerts",
  // };

  //const content = STATIC_POPIN_CONTENT;
  const content = data?.Content as PopinContent | undefined;

  const status = getPopinStatus(content);
  const active = status === "active";

  const [visible, setVisible] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const inactivityRef = useRef<ReturnType<typeof setTimeout>>();
  const inactivityCleanupRef = useRef<() => void>();
  const channelRef = useRef<BroadcastChannel | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const startInactivityWatcher = useCallback(() => {
    if (!active || isCompleted(active)) return;
    const INACTIVITY_MS = 72 * 60 * 60 * 1000; // 72 hours
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"] as const;

    const cleanup = () => {
      clearTimeout(inactivityRef.current);
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };

    const resetTimer = () => {
      clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        cleanup();
        setShowReminder(false);
        clearReminderVisible(active);
        setVisible(true);
        markSeen(active);
        broadcast(active, { type: "POPIN_OPEN" });
      }, INACTIVITY_MS);
    };

    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();
    inactivityCleanupRef.current = cleanup;
  }, [active]);

  const handleTabMessage = useCallback(
    (msg: TabMessage) => {
      switch (msg.type) {
        case "POPIN_SEEN":
          clearTimeout(timerRef.current);
          break;
        case "POPIN_OPEN":
          setShowReminder(false);
          setNewsletterSuccess(false);
          setVisible(true);
          break;
        case "POPIN_CLOSE":
          setVisible(false);
          setShowReminder(true);
          break;
        case "REMINDER_CLOSE":
          setShowReminder(false);
          startInactivityWatcher();
          break;
        case "REMINDER_REOPEN":
          setShowReminder(false);
          setVisible(true);
          break;
        case "SUPPRESSED":
          clearTimeout(timerRef.current);
          setVisible(false);
          setShowReminder(false);
          break;
      }
    },
    [startInactivityWatcher]
  );

  useEffect(() => {
    if (!active) return;
    const ch = getChannel();
    channelRef.current = ch;
    if (ch) ch.onmessage = (e: MessageEvent<TabMessage>) => handleTabMessage(e.data);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== "lapeyre_wp_state" || !e.newValue) return;
      try {
        const s = JSON.parse(e.newValue);

        if (s.bcFallback && Date.now() - s.bcFallback._t < 2_000) {
          handleTabMessage(s.bcFallback);
        }
      } catch {
        /* empty */
      }
    };

    window.addEventListener("storage", onStorage);
    const pending = readAndClearBcFallback(active);
    if (pending && Date.now() - pending._t < 2_000) {
      handleTabMessage(pending);
    }

    return () => {
      ch?.close();
      window.removeEventListener("storage", onStorage);
    };
  }, [active, handleTabMessage]);

  useEffect(() => {
    if (!active) return;

    if (shouldShowReminder(active) && !isSuppressed(active) && !isCompleted(active)) {
      setShowReminder(true);
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;
    if (isCompleted(active)) return;
    if (isSuppressed(active)) return;

    sanitizeStateOnMount(active);

    const { seen } = readState(active);
    if (seen) return;

    timerRef.current = setTimeout(() => {
      setVisible(true);
      markSeen(active);
      broadcast(active, { type: "POPIN_SEEN" });
    }, 10_000);

    return () => clearTimeout(timerRef.current);
  }, [active]);

  useEffect(() => {
    if (!visible) return;
    AsyncCall.sendWelcomePopupEvent("popin_welcome_display", {});
    const id = setTimeout(() => emailRef.current?.focus(), 300);
    return () => clearTimeout(id);
  }, [visible]);

  useEffect(() => {
    if (!showReminder) return;
  }, [showReminder]);

  useEffect(
    () => () => {
      clearTimeout(inactivityRef.current);
      inactivityCleanupRef.current?.();
    },
    []
  );

  useEffect(() => {
    if (status === "inactive") {
      clearPersistedState();
    } else if (status === "active") {
      writeState(false, { seen: true });
    }
  }, [status]);

  const handleClose = () => {
    setVisible(false);
    setShowReminder(true);
    setReminderVisible(active);
    broadcast(active, { type: "POPIN_CLOSE" });
    AsyncCall.sendWelcomePopupEvent("popin_welcome_close", {});
  };

  const handleClick = () => {
    handleClose();
    AsyncCall.sendWelcomePopupEvent("click_cta", { btn_label: "voir les offres" });
    navigate("/bons-plans");
  };

  const handleReminderClose = () => {
    setShowReminder(false);
    clearReminderVisible(active);
    setSuppression(active);
    startInactivityWatcher();
    broadcast(active, { type: "REMINDER_CLOSE" });
    AsyncCall.sendWelcomePopupEvent("popin_welcome_reminder_close", {});
  };

  const handleReminderReopen = () => {
    if (newsletterSuccess) return;
    clearTimeout(inactivityRef.current);
    inactivityCleanupRef.current?.();
    setShowReminder(false);
    clearReminderVisible(active);
    setNewsletterSuccess(false);
    setVisible(true);
    AsyncCall.sendWelcomePopupEvent("popin_welcome_reminder_display", {});
    broadcast(active, { type: "REMINDER_REOPEN" });
  };

  if (!active) return null;

  const popupInImage = isMobile
    ? storeUtil.getUrlPicture(data?.Content?.imageMobile, "")
    : storeUtil.getUrlPicture(data?.Content?.imageDesktop, "");

  return (
    <>
      {popinKeyframes}

      <Dialog
        open={visible}
        onClose={handleClose}
        maxWidth={false}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        aria-label="Offre de bienvenue"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 806,
            maxHeight: { xs: "90vh", sm: 520 },
            borderRadius: "2px",
            overflow: "hidden",
            animation: "wp-slide-up 0.35s cubic-bezier(0.34, 1.36, 0.64, 1)",
            display: "flex",
            margin: { xs: 2, sm: 0 },
            flexDirection: { xs: "column", sm: "row" },
          },
        }}
        sx={{ "& .MuiBackdrop-root": { backdropFilter: "blur(3px)", backgroundColor: "rgba(0,0,0,0.55)" } }}>
        <Box
          sx={{ flex: { xs: "0 0 195px", sm: "0 0 50%" }, position: "relative", minHeight: { xs: 195, sm: "unset" } }}>
          <ImageLayout src={popupInImage} alt="" width="100%" height="100%" fill className="all-universe-image" />
        </Box>

        <Box
          sx={{
            flex: 1,
            px: { xs: "20px", sm: "25px" },
            pt: { xs: "20px", sm: "36px" },
            pb: { xs: "10px", sm: "8px" },
            display: "flex",
            flexDirection: "column",
            position: { xs: "initial", sm: "relative" },
            color: "#ffffff",
            textAlign: "center",
            bgcolor: "#5D89A2",
            "& .MuiGrid-item .MuiInputBase-root": { borderRadius: "2px" },
          }}>
          <IconButton
            onClick={handleClose}
            aria-label="Fermer"
            size="small"
            sx={{ position: "absolute", top: 8, right: 8, color: "#fff", width: 32, height: 32 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {newsletterSuccess ? (
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <Typography
                sx={{
                  color: "inherit",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  fontSize: { xs: "34px", sm: "40px" },
                  lineHeight: { xs: "40px", sm: "45px" },
                  mt: { xs: 0, sm: "30px" },
                  mb: { xs: "20px", sm: "40px" },
                }}>
                bienvenue chez lapeyre !
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 500,
                  m: 0,
                  pb: { xs: "10px", sm: "20px" },
                  color: "inherit",
                  lineHeight: "26px",
                }}>
                Votre inscription est confirmée. Vous recevrez votre code avantage sous 24h.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 500,
                  m: 0,
                  pt: { xs: "5px", sm: "10px" },
                  color: "inherit",
                  lineHeight: "26px",
                }}>
                Profitez-en pour explorer nos offres dès maintenant.
              </Typography>
              <Box sx={{ mt: { xs: "10px", sm: "30px" }, mb: { xs: "15px", sm: "53px" } }}>
                <StyledButton variant="outline" color="black" type="submit" onClick={handleClick} welcomeOfferPopin>
                  voir les offres
                </StyledButton>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: { xs: "15px", md: "35px" } }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat, sans-serif",
                    textTransform: "uppercase",
                    textDecoration: "underline",
                    color: "inherit",
                    fontSize: { xs: "20px", sm: "24px", md: "29px" },
                    fontWeight: 900,
                    lineHeight: { xs: "20px", md: "normal" },
                    mt: { xs: "0px", md: "20px" },
                    mb: { xs: "15px", md: "41px" },
                  }}>
                  OFFRE DE BIENVENUE
                </Typography>
                <DiscountBadge value={content?.discount ?? ""} />
                <Typography
                  component="p"
                  dangerouslySetInnerHTML={{ __html: content?.descriptionHtml ?? "" }}
                  sx={{
                    fontSize: { xs: "14px", md: "17px" },
                    m: 0,
                    lineHeight: 1.4,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    color: "inherit",
                  }}
                />
              </Box>

              <Typography
                sx={{ fontSize: "15px", lineHeight: 1.5, m: 0, mb: "10px", fontWeight: 500, color: "inherit" }}>
                Inscrivez-vous à notre newsletter et recevez votre code avantage par email sous 24h.
              </Typography>

              <RegistrationNewsletter
                cid="welcome-popin"
                type="popin"
                onInput={(email) => {
                  if (email) {
                    AsyncCall.sendWelcomePopupEvent("popin_welcome_input", { email });
                  }
                }}
                onError={(error) => {
                  if (error) {
                    AsyncCall.sendWelcomePopupEvent("popin_welcome_error", {
                      error_type: error?.code ?? "validation",
                      error_message: error?.message ?? "",
                    });
                  }
                }}
                onSubmit={(email) => {
                  if (email) {
                    AsyncCall.sendWelcomePopupEvent("popin_welcome_submit", { email });
                  }
                }}
                onSuccess={() => {
                  setNewsletterSuccess(true);
                  setShowReminder(false);
                  markCompleted(active);
                  clearReminderVisible(active);
                }}
              />

              <Box sx={{ mt: "auto", pt: { xs: "10px", md: "15px" } }}>
                <StyledLink
                  to={`c/h/conditions-promotion${content?.anchor ? "#" + content?.anchor : ""}`}
                  key={content?.anchor}
                  onClick={handleClose}
                  style={{
                    fontSize: isMobile ? "10px" : "12px",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                    color: "#ffffff",
                    fontWeight: 400,
                  }}>
                  *Voir conditions des offres en cours
                </StyledLink>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {showReminder && !newsletterSuccess && !isCompleted(active) && (
        <SideReminder onReopen={handleReminderReopen} onDismiss={handleReminderClose} discount={content?.reminder} />
      )}
    </>
  );
};

export default WelcomeOfferPopin;
